import { z } from 'zod';

import { toInternalPath } from './internal-path';

/** 라우트 파라미터와 같은 값이어야 한다 — `/auth/{provider}` 로 그대로 나간다. */
export const SOCIAL_PROVIDERS = ['kakao', 'naver'] as const;
export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];

/** 화면에 그대로 나가는 제공사 이름. 제공사가 정한 표기를 따른다. */
export const PROVIDER_LABELS: Record<SocialProvider, string> = {
  kakao: '카카오',
  naver: '네이버',
};

export interface SocialHandoff {
  provider: SocialProvider;
  /** 로그인을 마치고 돌아갈 사이트 안의 경로. */
  next: string;
}

const STORAGE_KEY = 'autop-social-handoff';

const handoffSchema = z.object({
  provider: z.enum(SOCIAL_PROVIDERS),
  next: z.string(),
});

/**
 * 소셜 로그인으로 나가기 직전에 남기는 쪽지.
 *
 * 콜백은 `?status=` 하나만 들고 돌아온다 — 어느 제공사로 갔는지도, 원래 어디로 가려 했는지도
 * 서버가 돌려주지 않으므로 프론트가 기억하는 수밖에 없다. 그 탭의 그 흐름에서만 의미가 있어
 * 세션 저장소에 둔다.
 *
 * **사용자가 고칠 수 있는 값이다.** 문구와 목적지를 정하는 데만 쓰고 권한 판단에는 쓰지 않는다.
 */
export const rememberSocialHandoff = (handoff: SocialHandoff): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(handoff));
  } catch {
    // 저장소가 막힌 브라우저(사생활 보호 모드 등)가 있다. 쪽지는 문구와 목적지를 고르는
    //   보조 정보라, 없으면 기본값으로 흐르면 된다 — 로그인 자체는 이 값에 기대지 않는다.
  }
};

/** 쪽지가 없거나 손댄 흔적이 있으면 null. 값 하나만 어긋나도 통째로 버린다. */
export const readSocialHandoff = (): SocialHandoff | null => {
  const raw = readRaw();
  if (raw === null) {
    return null;
  }

  const parsed = handoffSchema.safeParse(parseJson(raw));
  if (!parsed.success) {
    return null;
  }

  return { provider: parsed.data.provider, next: toInternalPath(parsed.data.next) };
};

export const clearSocialHandoff = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // 위와 같다 — 지우지 못해도 다음 로그인이 덮어쓴다.
  }
};

const readRaw = (): string | null => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const parseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
